import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { AccountsService, ResearchedTransaction } from '@treasury/domain/accounts';
import { CheckImage } from '@treasury/domain/accounts/types/check-image.entity';
import '@treasury/omega/components/omega-button.js';
import '@treasury/omega/components/progress/omega-progress.js';
import { printIcon } from '@treasury/presentation/assets/icons';
import { exists, formatUsdCurrency, printNode } from '@treasury/utils';
import '@vaadin/grid';
import { GridActiveItemChangedEvent } from '@vaadin/grid';
import {
    columnBodyRenderer,
    GridColumnBodyLitRenderer,
    gridRowDetailsRenderer,
} from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-column.js';
import '@vaadin/grid/vaadin-grid-sort-column.js';
import { format } from 'date-fns';
import { css, html, LitElement, nothing, render } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

const tagName = 'check-lookup-table';
@customElement(tagName)
export class CheckLookupTable extends LitElement {
    @property()
    public transactions: ResearchedTransaction[] = [];

    @InjectProperty()
    private declare readonly accountsService: AccountsService;

    @state()
    private openedTransactions: ResearchedTransaction[] = [];

    @state()
    private checkImages: CheckImage[] = [];

    @state()
    private depositCheckImage?: CheckImage;

    @state()
    private loadingCheckImages = false;

    @state()
    private loadingDepositImages = false;

    @state()
    private transactionBeingFetched?: ResearchedTransaction;

    @state()
    private depositCheckOpened: CheckImage[] = [];

    render() {
        if (this.transactions.length === 0) {
            return nothing;
        }
        return html`<vaadin-grid
            all-rows-visible
            .items=${this.transactions}
            .detailsOpenedItems=${this.openedTransactions}
            ${gridRowDetailsRenderer<ResearchedTransaction>((transaction: ResearchedTransaction) =>
                this.renderTransactionDetail(transaction)
            )}
        >
            <vaadin-grid-sort-column
                header="Check/Ref Number"
                path="checkNumber"
                ${columnBodyRenderer(
                    this.renderCheckNumber as GridColumnBodyLitRenderer<ResearchedTransaction>,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Account"
                path="accountName"
                ${columnBodyRenderer(
                    this.renderAccount as GridColumnBodyLitRenderer<ResearchedTransaction>,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Check Date"
                path="date"
                ${columnBodyRenderer(
                    this.renderCheckDate as GridColumnBodyLitRenderer<ResearchedTransaction>,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                Header="Amount"
                path="amount"
                ${columnBodyRenderer(
                    this.renderAmount as GridColumnBodyLitRenderer<ResearchedTransaction>,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-column
                width="120px"
                flex-grow="0"
                frozen
                ${columnBodyRenderer(
                    this.renderCheckImageToggle as GridColumnBodyLitRenderer<ResearchedTransaction>,
                    []
                )}
            ></vaadin-grid-column>
        </vaadin-grid>`;
    }

    renderCheckNumber(transaction: ResearchedTransaction) {
        return html`<div class="py-1">${transaction.checkNumber}</div>`;
    }

    renderAccount(transaction: ResearchedTransaction) {
        return html`<div class="py-1">${transaction.accountName}</div>`;
    }

    renderCheckDate(transaction: ResearchedTransaction) {
        return html`<div class="py-1">${format(new Date(transaction.date), 'P')}</div>`;
    }

    renderAmount(transaction: ResearchedTransaction) {
        return html`<div class="py-1">${formatUsdCurrency(transaction.amount || 0)}</div>`;
    }

    renderCheckImageToggle(
        transaction: ResearchedTransaction,
        { detailsOpened }: { detailsOpened: boolean }
    ) {
        return html`<omega-button
            type="link"
            .loading=${this.transactionBeingFetched?.transactionId === transaction.transactionId &&
            this.loadingCheckImages}
            @click=${() => {
                this.updateOpenedTransaction(transaction, detailsOpened);
            }}
            >${detailsOpened ? 'Hide images' : 'View images'}</omega-button
        >`;
    }

    renderTransactionDetail(transaction: ResearchedTransaction) {
        return html`<div id="check detail">
            <div id="check-images">
                ${this.renderCheckImages(transaction)} ${this.renderDepositChecksTable(transaction)}
            </div>
            <div id="transaction-detail-footer">
                <omega-button
                    type="link"
                    @click=${() => {
                        this.printCheckImages(transaction);
                    }}
                    >${printIcon}</omega-button
                >
            </div>
        </div>`;
    }

    private renderDepositCheckImages() {
        if (!this.depositCheckImage) {
            return html`<div>No Check Images Available</div>`;
        }
        return html`<div>
            <img
                alt="Check Front"
                src=${`data:image/jpg;base64,${this.depositCheckImage.frontImage}`}
            />
            <img
                alt="Check Back"
                src=${`data:image/jpg;base64,${this.depositCheckImage.backImage}`}
            />
        </div>`;
    }

    private renderDepositChecksTable(transaction: ResearchedTransaction) {
        if (this.loadingCheckImages) {
            return nothing;
        }
        if (!this.checkImages.length) {
            return nothing;
        }
        return html`<div class="deposit-checks">
            <div class="check-images pt-3 px-3 border-t border-dashed border-container-page">
                <vaadin-grid
                    .items=${this.checkImages}
                    .detailsOpenedItems=${this.depositCheckOpened}
                    @active-item-changed=${(e: GridActiveItemChangedEvent<CheckImage>) => {
                        if (exists(e.detail.value)) {
                            this.updateOpenedDepositCheck(e.detail.value);
                        }
                    }}
                    ${gridRowDetailsRenderer<CheckImage>(() => this.renderDepositCheckImages())}
                    ><vaadin-grid-sort-column
                        auto-width
                        ${columnBodyRenderer(this.renderDepositCheckNumberColumn, [])}
                    ></vaadin-grid-sort-column>
                </vaadin-grid>
            </div>
        </div>`;
    }

    private renderDepositCheckNumberColumn(checkImage: CheckImage) {
        const index = this.checkImages.indexOf(checkImage) + 1;
        const { length } = this.checkImages;
        return html`<span class="text-sm text-header">Check Image ${index} of ${length}</span>`;
    }

    private renderCheckImages(transaction: ResearchedTransaction) {
        if (!transaction) {
            return nothing;
        }
        if (!transaction.source.checkImageNumber) {
            return nothing;
        }
        if (this.loadingCheckImages) {
            return html`<omega-progress card></omega-progress>`;
        }
        if (!this.checkImages.length) {
            return nothing;
        }
        return html`<img
                alt="Check Front"
                src=${`data:image/jpg;base64,${this.checkImages[0].frontImage}`}
            />
            <img
                alt="Check Back"
                src=${`data:image/jpg;base64,${this.checkImages[0].backImage}`}
            /> `;
    }

    async updateOpenedTransaction(transaction: ResearchedTransaction, detailsOpened = false) {
        this.openedTransactions = detailsOpened
            ? this.openedTransactions.filter(t => t.id !== transaction.id)
            : [...this.openedTransactions, transaction];
        if (!detailsOpened) {
            await this.fetchCheckImages(transaction);
        }
        this.openedTransactions = [...this.openedTransactions];
    }

    private async fetchCheckImages(transaction: ResearchedTransaction) {
        try {
            this.transactionBeingFetched = transaction;
            this.loadingCheckImages = true;
            this.checkImages = await this.accountsService.getCheckImages(
                transaction?.source?.checkImageNumber ?? '',
                transaction?.source?.isDebit ? 'check' : 'deposit'
            );
        } finally {
            this.loadingCheckImages = false;
            this.transactionBeingFetched = undefined;
        }
    }

    private async updateOpenedDepositCheck(check: CheckImage) {
        if (!check) {
            return;
        }
        const selectedCheck = check;
        if (selectedCheck.id === this.depositCheckOpened[0]?.id) {
            this.depositCheckOpened = [];
        } else {
            this.loadingDepositImages = true;
            const depositCheckImage = await this.accountsService.getCheckImages(
                selectedCheck.id,
                'check'
            );
            // eslint-disable-next-line prefer-destructuring
            this.depositCheckImage = depositCheckImage[0];
            this.depositCheckOpened = [selectedCheck];
            this.loadingDepositImages = false;
        }
    }

    private printCheckImages(transaction: ResearchedTransaction) {
        const printContainer = document.createElement('div');
        const printableData = html`<img
                alt="Check Front"
                src=${`data:image/jpg;base64,${this.checkImages[0].frontImage}`}
            />
            <img
                alt="Check Back"
                src=${`data:image/jpg;base64,${this.checkImages[0].backImage}`}
            /> `;
        render(printableData, printContainer);
        const title = `Check Lookup Account:${transaction.accountName} - Date:${format(new Date(transaction.date), 'P')}`;
        printNode(title, printContainer);
    }

    static get styles() {
        return [
            css`
                :host {
                    display: block;
                    padding: 10px 5px 5px 20px;
                    width: 75vw;
                }
                vaadin-grid > * {
                    font-size: 14px;
                }
                vaadin-grid-sort-column {
                    font-size: 14px;
                }
                #transaction-detail-footer {
                    display: flex;
                    justify-content: flex-end;
                    border-top: 1px solid #ccc;
                    margin-top: 10px;
                    background-color: #fff;
                    position: sticky;
                    bottom: 0;
                    z-index: 1;
                    left: 0;
                }
            `,
        ];
    }
}
declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CheckLookupTable;
    }
}
